import { useEffect, useState, useRef, useCallback } from 'react';
import { transactionApi, currencyApi, categoryApi, goldApi, silverApi, type TransactionPayload } from '../lib/api';
import type { Asset, Category, CustomCurrency, GoldMerchant, GoldPrice, GoldType, SilverMerchant, SilverPrice, SilverType, Transaction, TransactionType } from '@asset-mgmt/shared';
import { useToast } from '../context/ToastContext';

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtAmt(amount: number) {
  return amount.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function evaluateExpr(expr: string): number | null {
  const cleaned = expr.replace(/[,_\s]/g, '');
  if (!/^[\d.+\-*/()]+$/.test(cleaned)) return null;
  if (!/[+\-*/]/.test(cleaned)) return null;
  try {
    const result = Function(`"use strict"; return (${cleaned})`)() as number;
    if (typeof result !== 'number' || !isFinite(result)) return null;
    return Math.round(result * 100) / 100;
  } catch {
    return null;
  }
}

function useThrottle<T extends (...args: never[]) => void>(fn: T, delay: number): T {
  const lastRun = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  return useCallback((...args: Parameters<T>) => {
    const now = Date.now();
    const remaining = delay - (now - lastRun.current);
    clearTimeout(timer.current);
    if (remaining <= 0) {
      lastRun.current = now;
      fn(...args);
    } else {
      timer.current = setTimeout(() => {
        lastRun.current = Date.now();
        fn(...args);
      }, remaining);
    }
  }, [fn, delay]) as T;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-gray-700 dark:text-gray-300">{label}</span>
      {children}
    </label>
  );
}

const inp =
  'w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/50 px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100 outline-none transition-colors placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 dark:focus:ring-primary-900/30 dark:[color-scheme:dark]';

// ── Types ──────────────────────────────────────────────────────────────────────

type FormState = {
  assetId: string;
  toAssetId: string;
  type: TransactionType;
  amount: string;
  currency: string;
  categoryId: string;
  note: string;
  description: string;
  date: string;
  // Gold-specific
  goldQuantity: string;
  unitPrice: string;
  merchantId: string;
  goldType: GoldType;
  /** Cash/bank asset to debit (buy) or credit (sell) alongside this gold/silver transaction */
  fundAssetId: string;
  // Silver-specific
  silverQuantity: string;
  silverType: SilverType;
};

// ── Component ──────────────────────────────────────────────────────────────────

export function TransactionModal({
  assets,
  initialData,
  onClose,
  onBankSavingSelected,
}: {
  assets: Asset[];
  initialData: Transaction | null;
  onClose: () => void;
  onBankSavingSelected?: (asset: Asset) => void;
}) {
  const toast = useToast();
  const now   = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const isEdit = initialData !== null;

  const [form, setForm] = useState<FormState>({
    assetId:     initialData?.assetId     ?? assets[0]?.id ?? '',
    toAssetId:   initialData?.toAssetId   ?? '',
    type:        initialData?.type        ?? 'expense',
    amount:      initialData?.amount != null ? String(initialData.amount) : '',
    currency:    initialData?.currency    ?? assets[0]?.currency ?? 'USD',
    categoryId:  initialData?.categoryId  ?? 'general',
    note:        initialData?.note        ?? '',
    description: initialData?.description ?? '',
    date:        initialData?.date        ?? today,
    // Gold-specific
    goldQuantity: initialData?.goldQuantity != null ? String(initialData.goldQuantity) : '',
    unitPrice:    initialData?.unitPrice    != null ? String(initialData.unitPrice)    : '',
    merchantId:   initialData?.merchantId   ?? 'doji',
    goldType:     initialData?.goldType     ?? 'ring',
    fundAssetId:  initialData?.fundAssetId  ?? '',
    // Silver-specific
    silverQuantity: initialData?.silverQuantity != null ? String(initialData.silverQuantity) : '',
    silverType:     initialData?.silverType     ?? 'bar',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const [standardCurrencies, setStandardCurrencies] = useState<string[]>([]);
  const [customCurrencies,   setCustomCurrencies]   = useState<CustomCurrency[]>([]);
  const [categories,         setCategories]         = useState<Category[]>([]);
  const [conversionPreview,  setConversionPreview]  = useState<string | null>(null);
  // Gold state
  const [merchants,          setMerchants]          = useState<GoldMerchant[]>([]);
  const [goldPrice,          setGoldPrice]          = useState<GoldPrice | null>(null);
  const [goldPriceFetching,  setGoldPriceFetching]  = useState(false);
  // Silver state
  const [silverMerchants,       setSilverMerchants]       = useState<SilverMerchant[]>([]);
  const [silverPrice,           setSilverPrice]           = useState<SilverPrice | null>(null);
  const [silverPriceFetching,   setSilverPriceFetching]   = useState(false);
  // Note auto-suggestion
  const [noteSuggestions,       setNoteSuggestions]       = useState<string[]>([]);
  const [showNoteSuggestions,   setShowNoteSuggestions]   = useState(false);
  const noteInputRef = useRef<HTMLInputElement>(null);

  const fetchNoteSuggestions = useCallback((q: string) => {
    if (q.length < 2) { setNoteSuggestions([]); return; }
    transactionApi.searchNotes(q).then(setNoteSuggestions).catch(() => {});
  }, []);
  const throttledNoteSearch = useThrottle(fetchNoteSuggestions, 1000);

  useEffect(() => {
    Promise.all([currencyApi.listStandard(), currencyApi.listCustom()])
      .then(([std, custom]) => { setStandardCurrencies(std); setCustomCurrencies(custom); })
      .catch(() => {});
    categoryApi.list().then(setCategories).catch(() => {});
  }, []);

  // Categories available for the selected transaction type (type-specific + 'any').
  const typeCategories = categories.filter((cat) => cat.type === form.type || cat.type === 'any');

  // Default the category once the list loads, or when switching to a type whose
  // categories don't include the current selection. Skip when editing an existing
  // transaction whose category is already valid.
  useEffect(() => {
    if (categories.length === 0) return;
    const valid = typeCategories.some((cat) => cat.id === form.categoryId);
    if (!valid) {
      // Fall back to any existing category so transfers (no type-specific picker)
      // and edited legacy transactions still satisfy the required categoryId.
      setForm((prev) => ({ ...prev, categoryId: typeCategories[0]?.id ?? categories[0]?.id ?? '' }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, form.type]);

  // Load merchants list when a gold asset is selected
  const selectedAsset = assets.find((a) => a.id === form.assetId);
  const isGold   = selectedAsset?.type === 'gold';
  const isSilver = selectedAsset?.type === 'silver';

  useEffect(() => {
    if (!isGold) return;
    goldApi.getMerchants().then(setMerchants).catch(() => {});
  }, [isGold]);

  useEffect(() => {
    if (!isSilver) return;
    silverApi.getMerchants().then(setSilverMerchants).catch(() => {});
  }, [isSilver]);

  // Fetch gold price when merchant, goldType, or transaction type changes (gold assets only)
  useEffect(() => {
    if (!isGold || !form.merchantId) return;
    setGoldPriceFetching(true);
    goldApi.getPrice(form.merchantId, form.goldType)
      .then((price) => {
        setGoldPrice(price);
        // Auto-fill unit price:
        //   buying (income)  → user pays the merchant's sell price
        //   selling (expense) → user receives the merchant's buy price
        const autoPrice = form.type === 'income' ? price.sellPrice : price.buyPrice;
        setForm((prev) => ({ ...prev, unitPrice: String(autoPrice) }));
      })
      .catch(() => setGoldPrice(null))
      .finally(() => setGoldPriceFetching(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGold, form.merchantId, form.goldType, form.type]);

  // Fetch silver price when merchant, silverType, or transaction type changes (silver assets only)
  useEffect(() => {
    if (!isSilver || !form.merchantId) return;
    setSilverPriceFetching(true);
    silverApi.getPrice(form.merchantId, form.silverType)
      .then((price) => {
        setSilverPrice(price);
        const autoPrice = form.type === 'income' ? price.sellPrice : price.buyPrice;
        setForm((prev) => ({ ...prev, unitPrice: String(autoPrice) }));
      })
      .catch(() => setSilverPrice(null))
      .finally(() => setSilverPriceFetching(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSilver, form.merchantId, form.silverType, form.type]);

  useEffect(() => {
    const selectedAsset = assets.find((a) => a.id === form.assetId);
    const walletCurrency = selectedAsset?.currency ?? '';
    const amt = parseFloat(form.amount);
    if (!walletCurrency || form.currency === walletCurrency || isNaN(amt) || amt <= 0) {
      setConversionPreview(null);
      return;
    }
    let stdTxnCurrency = form.currency;
    let stdAmount = amt;
    const txnCustom = customCurrencies.find((c) => c.code === form.currency);
    if (txnCustom) { stdAmount = amt * txnCustom.multiplier; stdTxnCurrency = txnCustom.baseCode; }
    let stdWallet = walletCurrency;
    let walletDivisor = 1;
    const walletCustom = customCurrencies.find((c) => c.code === walletCurrency);
    if (walletCustom) { stdWallet = walletCustom.baseCode; walletDivisor = walletCustom.multiplier; }
    if (stdTxnCurrency === stdWallet) {
      setConversionPreview(`≈ ${fmtAmt(stdAmount / walletDivisor)} ${walletCurrency}`);
      return;
    }
    currencyApi.getRates(stdTxnCurrency)
      .then((rates) => {
        const rate = rates.rates[stdWallet];
        if (rate == null) { setConversionPreview(null); return; }
        setConversionPreview(`≈ ${fmtAmt((stdAmount * rate) / walletDivisor)} ${walletCurrency}`);
      })
      .catch(() => setConversionPreview(null));
  }, [form.currency, form.amount, form.assetId, customCurrencies, assets]);

  function set<K extends keyof FormState>(key: K, val: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: val }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (isGold) {
      const qty = parseFloat(form.goldQuantity);
      const price = parseFloat(form.unitPrice);
      if (isNaN(qty) || qty <= 0) { setError('Quantity must be a positive number.'); return; }
      if (isNaN(price) || price <= 0) { setError('Unit price must be a positive number.'); return; }
      if (!form.merchantId) { setError('Please select a merchant.'); return; }
      if (form.type === 'expense' && selectedAsset && !selectedAsset.isLiability) {
        let available = selectedAsset.balance;
        if (isEdit && initialData && initialData.assetId === form.assetId && initialData.type === 'expense' && initialData.goldQuantity) {
          available += initialData.goldQuantity;
        }
        if (available - qty < 0) {
          setError(`Insufficient gold balance. You have ${selectedAsset.balance.toLocaleString(undefined, { maximumFractionDigits: 2 })} chỉ but are trying to sell ${qty} chỉ.`);
          return;
        }
      }
      setError('');
      setSaving(true);
      const totalAmount = qty * price;
      const payload: TransactionPayload = {
        assetId:      form.assetId,
        categoryId:   form.categoryId,
        type:         form.type,
        amount:       totalAmount,
        currency:     'VND',
        date:         form.date,
        goldQuantity: qty,
        unitPrice:    price,
        merchantId:   form.merchantId,
        goldType:     form.goldType,
        ...(form.fundAssetId ? { fundAssetId: form.fundAssetId } : {}),
        note: form.note || `${qty} chỉ`,
        ...(form.description ? { description: form.description } : {}),
      };
      try {
        if (isEdit) {
          await transactionApi.update(initialData!.id, { ...payload, originalDate: initialData!.date });
          toast.success('Transaction updated.');
        } else {
          await transactionApi.create(payload);
          toast.success('Transaction saved.');
        }
        onClose();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save transaction.');
        setSaving(false);
      }
      return;
    }

    if (isSilver) {
      const qty = parseFloat(form.silverQuantity);
      const price = parseFloat(form.unitPrice);
      const unit = form.silverType === 'bar1kg' ? 'kg' : 'lượng';
      if (isNaN(qty) || qty <= 0) { setError('Quantity must be a positive number.'); return; }
      if (isNaN(price) || price <= 0) { setError('Unit price must be a positive number.'); return; }
      if (!form.merchantId) { setError('Please select a merchant.'); return; }
      if (form.type === 'expense' && selectedAsset && !selectedAsset.isLiability) {
        let available = selectedAsset.balance;
        if (isEdit && initialData && initialData.assetId === form.assetId && initialData.type === 'expense' && initialData.silverQuantity) {
          available += initialData.silverQuantity;
        }
        if (available - qty < 0) {
          setError(`Insufficient silver balance. You have ${selectedAsset.balance.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${unit} but are trying to sell ${qty} ${unit}.`);
          return;
        }
      }
      setError('');
      setSaving(true);
      const totalAmount = qty * price;
      const payload: TransactionPayload = {
        assetId:        form.assetId,
        categoryId:     form.categoryId,
        type:           form.type,
        amount:         totalAmount,
        currency:       'VND',
        date:           form.date,
        silverQuantity: qty,
        silverType:     form.silverType,
        unitPrice:      price,
        merchantId:     form.merchantId,
        ...(form.fundAssetId ? { fundAssetId: form.fundAssetId } : {}),
        note: form.note || `${qty} ${unit}`,
        ...(form.description ? { description: form.description } : {}),
      };
      try {
        if (isEdit) {
          await transactionApi.update(initialData!.id, { ...payload, originalDate: initialData!.date });
          toast.success('Transaction updated.');
        } else {
          await transactionApi.create(payload);
          toast.success('Transaction saved.');
        }
        onClose();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save transaction.');
        setSaving(false);
      }
      return;
    }

    const amt = evaluateExpr(form.amount) ?? parseFloat(form.amount);
    if (isNaN(amt) || amt <= 0) { setError('Amount must be a positive number.'); return; }
    if (form.type === 'transfer' && !form.toAssetId) { setError('Please select a destination asset.'); return; }
    // Balance check: block expense/transfer if it would make balance negative (non-liability only)
    // When editing, add back the original amount since it's already reflected in the current balance
    if ((form.type === 'expense' || form.type === 'transfer') && selectedAsset && !selectedAsset.isLiability) {
      const txnCustom = customCurrencies.find((c) => c.code === form.currency);
      const resolvedAmt = txnCustom ? amt * txnCustom.multiplier : amt;
      const assetCustom = customCurrencies.find((c) => c.code === selectedAsset.currency);
      const assetBaseCurrency = assetCustom ? assetCustom.baseCode : selectedAsset.currency;
      const txnBaseCurrency   = txnCustom   ? txnCustom.baseCode   : form.currency;
      if (txnBaseCurrency === assetBaseCurrency) {
        let assetBaseBalance = assetCustom ? selectedAsset.balance * assetCustom.multiplier : selectedAsset.balance;
        if (isEdit && initialData && initialData.assetId === form.assetId && (initialData.type === 'expense' || initialData.type === 'transfer')) {
          assetBaseBalance += initialData.amount;
        }
        if (assetBaseBalance - resolvedAmt < 0) {
          setError(`Insufficient balance. Current balance is ${selectedAsset.balance.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${selectedAsset.currency}.`);
          return;
        }
      }
    }
    setError('');
    setSaving(true);

    const txnCustom = customCurrencies.find((c) => c.code === form.currency);
    const finalAmount   = txnCustom ? amt * txnCustom.multiplier : amt;
    const finalCurrency = txnCustom ? txnCustom.baseCode : form.currency;

    try {
      const base: TransactionPayload = {
        assetId:    form.assetId,
        categoryId: form.categoryId,
        type:       form.type,
        amount:     finalAmount,
        currency:   finalCurrency,
        date:       form.date,
        ...(form.type === 'transfer' && form.toAssetId ? { toAssetId: form.toAssetId } : {}),
        ...(form.note        ? { note:        form.note        } : {}),
        ...(form.description ? { description: form.description } : {}),
      };
      if (isEdit) {
        await transactionApi.update(initialData!.id, { ...base, originalDate: initialData!.date });
        toast.success('Transaction updated.');
      } else {
        await transactionApi.create(base);
        toast.success('Transaction saved.');
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save transaction.');
      setSaving(false);
    }
  }

  const toAssetOptions = assets.filter((a) => a.id !== form.assetId);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-md overflow-hidden rounded-t-2xl sm:rounded-2xl bg-white dark:bg-gray-800 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700 px-6 py-4">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {isEdit ? 'Edit Transaction' : 'New Transaction'}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4 overflow-y-auto max-h-[80vh] p-6">
          {/* Type selector */}
          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Type</p>
            <div className="grid grid-cols-3 gap-1.5 rounded-xl bg-gray-100 dark:bg-gray-900/60 p-1">
              {/* Gold/silver assets use income=buy, expense=sell */}
              {(isGold || isSilver ? ['income', 'expense'] as const : ['expense', 'income', 'transfer'] as const).map((t) => {
                const active = form.type === t;
                const activeColor =
                  t === 'income'   ? 'text-emerald-600 dark:text-emerald-400'
                : t === 'expense'  ? 'text-red-600 dark:text-red-400'
                : 'text-blue-600 dark:text-blue-400';
                const label = (isGold || isSilver)
                  ? (t === 'income' ? 'Buy' : 'Sell')
                  : t.charAt(0).toUpperCase() + t.slice(1);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => set('type', t)}
                    className={`rounded-lg py-2 text-sm font-medium capitalize transition-all ${
                      active
                        ? `bg-white dark:bg-gray-700 shadow-sm ${activeColor}`
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* From asset */}
          <Field label="From Asset">
            <select
              value={form.assetId}
              onChange={(e) => {
                const asset = assets.find((a) => a.id === e.target.value);
                if (asset?.type === 'bank_saving' && onBankSavingSelected) {
                  onBankSavingSelected(asset);
                  return;
                }
                setForm((prev) => ({ ...prev, assetId: e.target.value, currency: asset?.currency ?? prev.currency }));
              }}
              className={inp}
            >
              {assets.map((a) => (
                <option key={a.id} value={a.id}>{a.name} ({a.type === 'gold' ? 'chỉ' : a.type === 'silver' ? 'lượng' : a.currency})</option>
              ))}
            </select>
          </Field>
          {/* Current balance display */}
          {selectedAsset && (
            <p className="-mt-2 text-xs text-gray-500 dark:text-gray-400">
              Current balance: 
              <span className={`font-semibold tabular-nums ${
                selectedAsset.balance < 0 ? 'text-red-500 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'
              }`}>
                {selectedAsset.balance.toLocaleString(undefined, { maximumFractionDigits: 2 })} {selectedAsset.type === 'gold' ? 'chỉ' : selectedAsset.type === 'silver' ? 'lượng' : selectedAsset.currency}
              </span>
            </p>
          )}

          {/* Gold-specific fields */}
          {isGold && (
            <>
              {/* Merchant selector */}
              <Field label="Merchant">
                <select
                  value={form.merchantId}
                  onChange={(e) => set('merchantId', e.target.value)}
                  className={inp}
                >
                  {merchants.length === 0 && (
                    <option value="doji">DOJI</option>
                  )}
                  {merchants.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </Field>

              {/* Gold type selector */}
              <div>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Gold Type</p>
                <div className="grid grid-cols-2 gap-1.5 rounded-xl bg-gray-100 dark:bg-gray-900/60 p-1">
                  {([['ring', 'Nhẫn Tròn'], ['bar', 'Vàng Miếng']] as const).map(([val, label]) => {
                    const active = form.goldType === val;
                    return (
                      <button
                        key={val}
                        type="button"
                        onClick={() => set('goldType', val)}
                        className={`rounded-lg py-2 text-sm font-medium transition-all ${
                          active
                            ? 'bg-white dark:bg-gray-700 shadow-sm text-amber-600 dark:text-amber-400'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Fund asset — cash/bank account to debit or credit */}
              <Field label={form.type === 'income' ? 'Deduct funds from (optional)' : 'Deposit proceeds to (optional)'}>
                <select
                  value={form.fundAssetId}
                  onChange={(e) => set('fundAssetId', e.target.value)}
                  className={inp}
                >
                  <option value="">— None —</option>
                  {assets
                    .filter((a) => a.id !== form.assetId && a.type !== 'gold')
                    .map((a) => (
                      <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>
                    ))}
                </select>
              </Field>

              {/* Live gold prices display */}
              {goldPriceFetching && (
                <p className="text-xs text-gray-500 dark:text-gray-400">Fetching current prices…</p>
              )}
              {goldPrice && !goldPriceFetching && (
                <div className="rounded-xl border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-4 py-2.5 text-sm">
                  <p className="font-medium text-amber-800 dark:text-amber-300">{goldPrice.productName}</p>
                  <div className="mt-1 flex gap-4 text-amber-700 dark:text-amber-400">
                    <span className={form.type === 'income' ? 'font-bold text-amber-900 dark:text-amber-200 underline decoration-dotted' : ''}>
                      Mua: {goldPrice.sellPrice.toLocaleString()} ₫/chỉ
                    </span>
                    <span className={form.type === 'expense' ? 'font-bold text-amber-900 dark:text-amber-200 underline decoration-dotted' : ''}>
                      Bán: {goldPrice.buyPrice.toLocaleString()} ₫/chỉ
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-amber-600 dark:text-amber-500">
                    Updated {new Date(goldPrice.fetchedAt).toLocaleTimeString()}
                  </p>
                </div>
              )}

              {/* Quantity in chỉ */}
              <div className="grid grid-cols-5 gap-3">
                <div className="col-span-3">
                  <Field label="Quantity (chỉ)">
                    <input
                      required
                      type="number"
                      step="0.01"
                      min="0.01"
                      placeholder="e.g. 1.5"
                      value={form.goldQuantity}
                      onChange={(e) => set('goldQuantity', e.target.value)}
                      className={inp}
                    />
                  </Field>
                </div>
                <div className="col-span-2">
                  <Field label="Unit Price (VND/chỉ)">
                    <input
                      required
                      type="number"
                      step="1000"
                      min="1000"
                      placeholder="e.g. 16300000"
                      value={form.unitPrice}
                      onChange={(e) => set('unitPrice', e.target.value)}
                      className={inp}
                    />
                  </Field>
                </div>
              </div>

              {/* Computed total */}
              {(() => {
                const qty = parseFloat(form.goldQuantity);
                const price = parseFloat(form.unitPrice);
                if (!isNaN(qty) && !isNaN(price) && qty > 0 && price > 0) {
                  return (
                    <p className="-mt-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                      Total: {(qty * price).toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })}
                    </p>
                  );
                }
                return null;
              })()}
            </>
          )}

          {/* Silver-specific fields */}
          {isSilver && (
            <>
              {/* Merchant selector */}
              <Field label="Merchant">
                <select
                  value={form.merchantId}
                  onChange={(e) => set('merchantId', e.target.value)}
                  className={inp}
                >
                  {silverMerchants.length === 0 && (
                    <option value="phu-quy">Phu Quy</option>
                  )}
                  {silverMerchants.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </Field>

              {/* Silver type selector */}
              <div>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Silver Type</p>
                <div className="grid grid-cols-2 gap-1.5 rounded-xl bg-gray-100 dark:bg-gray-900/60 p-1">
                  {([['bar', 'Bạc miếng 1 lượng'], ['bar1kg', 'Bạc miếng 1 kg']] as const).map(([val, label]) => {
                    const active = form.silverType === val;
                    return (
                      <button
                        key={val}
                        type="button"
                        onClick={() => set('silverType', val)}
                        className={`rounded-lg py-2 text-sm font-medium transition-all ${
                          active
                            ? 'bg-white dark:bg-gray-700 shadow-sm text-slate-600 dark:text-slate-300'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Fund asset — cash/bank account to debit or credit */}
              <Field label={form.type === 'income' ? 'Deduct funds from (optional)' : 'Deposit proceeds to (optional)'}>
                <select
                  value={form.fundAssetId}
                  onChange={(e) => set('fundAssetId', e.target.value)}
                  className={inp}
                >
                  <option value="">— None —</option>
                  {assets
                    .filter((a) => a.id !== form.assetId && a.type !== 'gold' && a.type !== 'silver')
                    .map((a) => (
                      <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>
                    ))}
                </select>
              </Field>

              {/* Live silver prices display */}
              {silverPriceFetching && (
                <p className="text-xs text-gray-500 dark:text-gray-400">Fetching current prices…</p>
              )}
              {silverPrice && !silverPriceFetching && (
                <div className="rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/40 px-4 py-2.5 text-sm">
                  <p className="font-medium text-slate-700 dark:text-slate-300">{silverPrice.productName}</p>
                  <div className="mt-1 flex gap-4 text-slate-600 dark:text-slate-400">
                    <span className={form.type === 'income' ? 'font-bold text-slate-800 dark:text-slate-200 underline decoration-dotted' : ''}>
                      Mua: {silverPrice.sellPrice.toLocaleString()} ₫/{form.silverType === 'bar1kg' ? 'kg' : 'lượng'}
                    </span>
                    <span className={form.type === 'expense' ? 'font-bold text-slate-800 dark:text-slate-200 underline decoration-dotted' : ''}>
                      Bán: {silverPrice.buyPrice.toLocaleString()} ₫/{form.silverType === 'bar1kg' ? 'kg' : 'lượng'}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-500">
                    Updated {new Date(silverPrice.fetchedAt).toLocaleTimeString()}
                  </p>
                </div>
              )}

              {/* Quantity */}
              <div className="grid grid-cols-5 gap-3">
                <div className="col-span-3">
                  <Field label={`Quantity (${form.silverType === 'bar1kg' ? 'kg' : 'lượng'})`}>
                    <input
                      required
                      type="number"
                      step="0.001"
                      min="0.001"
                      placeholder="e.g. 1"
                      value={form.silverQuantity}
                      onChange={(e) => set('silverQuantity', e.target.value)}
                      className={inp}
                    />
                  </Field>
                </div>
                <div className="col-span-2">
                  <Field label={`Unit Price (VND/${form.silverType === 'bar1kg' ? 'kg' : 'lượng'})`}>
                    <input
                      required
                      type="number"
                      step="1000"
                      min="1000"
                      placeholder="e.g. 2870000"
                      value={form.unitPrice}
                      onChange={(e) => set('unitPrice', e.target.value)}
                      className={inp}
                    />
                  </Field>
                </div>
              </div>

              {/* Computed total */}
              {(() => {
                const qty = parseFloat(form.silverQuantity);
                const price = parseFloat(form.unitPrice);
                if (!isNaN(qty) && !isNaN(price) && qty > 0 && price > 0) {
                  return (
                    <p className="-mt-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                      Total: {(qty * price).toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })}
                    </p>
                  );
                }
                return null;
              })()}
            </>
          )}

          {/* To asset (transfer only — non-gold, non-silver) */}
          {form.type === 'transfer' && !isGold && !isSilver && (
            <Field label="To Asset">
              <select value={form.toAssetId} onChange={(e) => set('toAssetId', e.target.value)} className={inp}>
                <option value="">— Select destination —</option>
                {toAssetOptions.map((a) => (
                  <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>
                ))}
              </select>
            </Field>
          )}

          {/* Amount + Currency (non-gold, non-silver assets only) */}
          {!isGold && !isSilver && (
          <div>
            <div className="grid grid-cols-5 gap-3">
              <div className="col-span-3">
                <Field label="Amount">
                  <input
                    required
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00 or 100+50"
                    value={form.amount}
                    onChange={(e) => set('amount', e.target.value)}
                    className={inp}
                  />
                  {(() => {
                    const result = evaluateExpr(form.amount);
                    if (result === null) return null;
                    return (
                      <p className="mt-1 text-xs text-primary-600 dark:text-primary-400">
                        = {fmtAmt(result)}
                      </p>
                    );
                  })()}
                </Field>
              </div>
              <div className="col-span-2">
                <Field label="Currency">
                  <select
                    value={form.currency}
                    onChange={(e) => set('currency', e.target.value)}
                    className={inp}
                  >
                    {!standardCurrencies.includes(form.currency) &&
                     !customCurrencies.find((c) => c.code === form.currency) && (
                      <option value={form.currency}>{form.currency}</option>
                    )}
                    {customCurrencies.length > 0 && (
                      <optgroup label="Custom">
                        {customCurrencies.map((c) => (
                          <option key={c.id} value={c.code}>{c.code} — {c.name}</option>
                        ))}
                      </optgroup>
                    )}
                    {standardCurrencies.length > 0 && (
                      <optgroup label="Standard">
                        {standardCurrencies.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </Field>
              </div>
            </div>
            {conversionPreview && (
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">{conversionPreview}</p>
            )}
          </div>
          )}

          {/* Date */}
          <Field label="Date">
            <input
              type="date"
              required
              value={form.date}
              onChange={(e) => set('date', e.target.value)}
              className={inp}
            />
          </Field>

          {/* Category (income/expense only) */}
          {form.type !== 'transfer' && (
            <div>
              <p className="mb-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">Category</p>
              {typeCategories.length === 0 ? (
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  No categories yet — add some in Settings.
                </p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {typeCategories.map((cat) => {
                    const active = form.categoryId === cat.id;
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => set('categoryId', cat.id)}
                        className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-all ${
                          active
                            ? 'border-transparent text-white shadow-sm'
                            : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                        style={active ? { backgroundColor: cat.color } : undefined}
                      >
                        {cat.icon && <span>{cat.icon}</span>}
                        <span>{cat.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Note */}
          <Field label="Note (optional)">
            <div className="relative">
              <input
                ref={noteInputRef}
                value={form.note}
                onChange={(e) => {
                  set('note', e.target.value);
                  throttledNoteSearch(e.target.value);
                  setShowNoteSuggestions(true);
                }}
                onFocus={() => { if (noteSuggestions.length > 0) setShowNoteSuggestions(true); }}
                onBlur={() => { setTimeout(() => setShowNoteSuggestions(false), 150); }}
                placeholder="e.g. Grocery shopping"
                autoComplete="off"
                className={inp}
              />
              {showNoteSuggestions && noteSuggestions.length > 0 && (
                <ul className="absolute z-10 mt-1 max-h-40 w-full overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
                  {noteSuggestions.map((n) => (
                    <li key={n}>
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          set('note', n);
                          setShowNoteSuggestions(false);
                          setNoteSuggestions([]);
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors"
                      >
                        {n}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Field>

          {/* Description */}
          <Field label="Description (optional)">
            <textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Additional details…"
              rows={3}
              className={`${inp} resize-none`}
            />
          </Field>

          {/* Error */}
          {error && (
            <p className="rounded-xl bg-red-50 dark:bg-red-900/30 px-4 py-2.5 text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-xl border border-gray-200 dark:border-gray-700 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 rounded-xl bg-primary-600 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? 'Saving…' : isEdit ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
